import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "./EmailApi";
import "./WebmailAccessSection.css";

function WebmailAccessSection() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openingId, setOpeningId] = useState("");
  const [message, setMessage] = useState("");

  const safeList = useMemo(() => {
    return Array.isArray(list) ? list.filter(Boolean) : [];
  }, [list]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setMessage("");

      const data = await apiGet("/api/email/webmail-access");

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load webmail access.");
      }

      setList(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      setList([]);
      setMessage(error.message || "Webmail access fetch failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    const intervalId = window.setInterval(fetchData, 12000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchData]);

  const handleOpenWebmail = (item) => {
    const webmailUrl = item?.webmailUrl || item?.webmail_url || "";
    const itemId = item?.id || item?.email_address || item?.emailAddress || "";

    if (!webmailUrl) {
      setMessage("Webmail URL is not available for this account.");
      return;
    }

    setOpeningId(String(itemId));
    window.open(webmailUrl, "_blank", "noopener,noreferrer");

    window.setTimeout(() => {
      setOpeningId("");
    }, 800);
  };

  return (
    <div className="email-panel-card webmail-section">
      <div className="webmail-header">
        <div>
          <span className="webmail-badge">Webmail Access</span>
          <h2>Webmail Access</h2>
          <p>
            Open your business mailboxes from anywhere with secure browser-based
            webmail access.
          </p>
        </div>

        <div className="webmail-icon-card">🌐</div>
      </div>

      {message ? <output className="email-error-box webmail-alert">{message}</output> : null}

      <div className="webmail-summary-row">
        <div>
          <strong>{safeList.length}</strong>
          <span>Total Accounts</span>
        </div>

        <div>
          <strong>12s</strong>
          <span>Auto Refresh</span>
        </div>

        <div>
          <strong>{loading ? "..." : "Live"}</strong>
          <span>Status</span>
        </div>
      </div>

      <div className="email-panel-list webmail-list">
        {loading ? <p className="webmail-empty">Loading webmail access...</p> : null}

        {!loading && safeList.length === 0 ? (
          <p className="webmail-empty">No webmail access found.</p>
        ) : null}

        {safeList.map((item) => {
          const itemId = item.id || item.email_address || item.emailAddress;
          const emailAddress = item.email_address || item.emailAddress || "-";
          const displayName = item.display_name || item.displayName || "-";
          const accessStatus = item.accessStatus || item.status || "inactive";
          const smtpHost = item.smtp_host || item.smtpHost || "Default";
          const isOpening = openingId === String(itemId);

          return (
            <div className="email-panel-item webmail-item" key={itemId || emailAddress}>
              <div className="webmail-item-top">
                <div>
                  <h4>{emailAddress}</h4>
                  <p>Display Name: {displayName}</p>
                </div>

                <span className={`webmail-status ${String(accessStatus).toLowerCase()}`}>
                  {accessStatus}
                </span>
              </div>

              <div className="webmail-meta-grid">
                <p>SMTP Host: {smtpHost}</p>
                <p>Access Status: {accessStatus}</p>
              </div>

              <button
                type="button"
                className="email-panel-link webmail-open-btn"
                onClick={() => handleOpenWebmail(item)}
                disabled={isOpening}
              >
                {isOpening ? "Opening..." : "Open Webmail"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WebmailAccessSection;